'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { ZUORA_HPP_SUBMIT_URL } from './config';
import { loadZuora } from './zuora';
import {
  classifyZuoraError,
  RESPONSE_FROM_REQUEST_PAGE,
  type ZuoraError,
  type ZuoraLogLevel,
} from './classifyZuoraError';
import type { ZCallbackResponse } from './types';
import { ZuoraSession } from '../api/billing';

export type ZuoraHostedPageStatus = 'loading' | 'ready' | 'submitting' | 'error' | 'success';

/**
 * Structured failure event emitted on every failure lane — raw message plus
 * error-response data for observability. Omits session creds (`token`/`signature`).
 */
export type ZuoraFailureReport = {
  message: string;
  /** Zuora `errorCode`, or `null` for a load/timeout failure. */
  code: string | null;
  responseFrom?: string;
  /** Datadog severity to report at — see classifyZuoraError. */
  logLevel: ZuoraLogLevel;
};

export type UseZuoraHostedPageArgs = {
  /** When `null`, the hook stays in `loading` and renders nothing. */
  session: ZuoraSession | null;
  /**
   * DOM id of the render target. Zuora's HPM 2.0 lib looks it up by a hardcoded
   * id (`zuora_payment`); the host MUST render `<div id="zuora_payment" />`.
   */
  containerId: string;
  onSuccess: (refId?: string) => void;
  onFailure: (report: ZuoraFailureReport) => void;
  /**
   * Ask the owner for a fresh session. Every recoverable submit failure re-mints
   * through this (a used token can't be re-rendered). Capped by MAX_AUTO_RETRIES.
   */
  onRequestNewSession?: () => void;
  /** Timeout for Zuora's `onloadCallback`. */
  renderTimeoutMs?: number;
  /**
   * How long to wait after `submit()` for the result via `window.zuoraCallback`
   * before treating the submit as dropped and routing to the failure lane.
   */
  submitTimeoutMs?: number;
};

export type UseZuoraHostedPageResult = {
  status: ZuoraHostedPageStatus;
  error: ZuoraError | null;
  submit: () => void;
};

const DEFAULT_RENDER_TIMEOUT_MS = 15_000;

/**
 * Submit-result timeout. Generous so a slow-but-valid gateway round-trip beats
 * it; only a truly dropped submit (bridge stalled, parent unmounted) trips it.
 */
const DEFAULT_SUBMIT_TIMEOUT_MS = 30_000;

/**
 * How many times a recoverable error may auto-mint a fresh session before a
 * terminal banner. Reset on a clean render (`ready`), so submit-time failures
 * retry effectively without limit; only a render that keeps failing before
 * `ready` (a persistently bad session) accumulates toward the cap.
 */
const MAX_AUTO_RETRIES = 2;

const ZUORA_IFRAME_SANDBOX = 'allow-scripts allow-same-origin allow-forms';

/**
 * Owns the imperative HPM lifecycle inside a React component.
 *
 *   loading    → loadZuora() + Z.render running
 *   ready      → onloadCallback fired; the user can interact with the iframe
 *   submitting → Z.submit() called; awaiting window.zuoraCallback
 *   error      → terminal dead-end (render-page failure, load/timeout, re-mint
 *                cap exhausted): banner + Close, no iframe
 *   success    → zuoraCallback delivered { success: true }; terminal happy path
 *
 * classifyZuoraError routes failures into two lanes:
 *   - re-mint : recoverable submit failures ask the owner for a fresh session
 *     (a used token can't be re-rendered), capped (MAX_AUTO_RETRIES). Banner
 *     shows the classified message above the re-rendered form.
 *   - terminal: request-page failure, load/timeout, or cap exhaustion →
 *     dead-end banner + Close, no iframe.
 */
export const useZuoraHostedPage = ({
  session,
  containerId,
  onSuccess,
  onFailure,
  onRequestNewSession,
  renderTimeoutMs = DEFAULT_RENDER_TIMEOUT_MS,
  submitTimeoutMs = DEFAULT_SUBMIT_TIMEOUT_MS,
}: UseZuoraHostedPageArgs): UseZuoraHostedPageResult => {
  const [status, setStatus] = useState<ZuoraHostedPageStatus>('loading');
  const [error, setError] = useState<ZuoraError | null>(null);

  const onSuccessRef = useRef(onSuccess);
  const onFailureRef = useRef(onFailure);
  const onRequestNewSessionRef = useRef(onRequestNewSession);
  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);
  useEffect(() => {
    onFailureRef.current = onFailure;
  }, [onFailure]);
  useEffect(() => {
    onRequestNewSessionRef.current = onRequestNewSession;
  }, [onRequestNewSession]);

  const retryCountRef = useRef(0);

  // `submit()` lives outside the render effect but must reach that effect's
  // `fail` (its `cancelled` guard, `timeoutId`) to route a dropped submit. The
  // effect keeps this ref current; `submit` arms a timeout that calls through it.
  const failRef = useRef<
    ((input: { responseFrom?: string; code: string | null; message: string }) => void) | null
  >(null);
  const submitTimeoutRef = useRef<number | undefined>(undefined);

  // Session fields as primitives so the render effect's dep array stays stable.
  const tenantId = session?.tenantId ?? null;
  const pageId = session?.pageId ?? null;
  const key = session?.key ?? null;
  const accountId = session?.accountId ?? null;
  const token = session?.token ?? null;
  const signature = session?.signature ?? null;

  useEffect(() => {
    if (!tenantId || !pageId || !key || !accountId || !token || !signature) {
      return;
    }

    let cancelled = false;
    setStatus('loading');
    // Do NOT clear `error` here: on a re-mint the banner persists above the
    // re-rendered form. It clears only on success or the next failure.

    // Clear any iframe left from a previous render (re-mint, StrictMode remount).
    const container = document.getElementById(containerId);
    container?.replaceChildren();

    // Sandbox Zuora's iframe as soon as it mounts.
    const sandboxObserver = container
      ? new MutationObserver(() => {
          const iframe = container.querySelector('iframe');
          if (iframe && iframe.getAttribute('sandbox') !== ZUORA_IFRAME_SANDBOX) {
            iframe.setAttribute('sandbox', ZUORA_IFRAME_SANDBOX);
          }
        })
      : null;
    sandboxObserver?.observe(container!, { childList: true });

    // Single failure path: report via `onFailure`, then route by lane (re-mint
    // vs terminal — see the header doc). `fail` closes over `timeoutId` (const
    // below); safe — it only runs later from async callbacks.
    const fail = (input: { responseFrom?: string; code: string | null; message: string }) => {
      if (cancelled) return;
      window.clearTimeout(timeoutId);
      const {
        lane,
        error: classified,
        logLevel,
      } = classifyZuoraError({
        responseFrom: input.responseFrom,
        code: input.code,
        message: input.message,
      });
      setError(classified);
      onFailureRef.current({
        message: input.message,
        code: input.code,
        responseFrom: input.responseFrom,
        logLevel,
      });

      if (
        lane === 're-mint' &&
        retryCountRef.current < MAX_AUTO_RETRIES &&
        onRequestNewSessionRef.current
      ) {
        retryCountRef.current += 1;
        setStatus('loading');
        onRequestNewSessionRef.current();
        return;
      }

      // terminal, or re-mint with cap exhausted / no owner wired.
      setStatus('error');
    };
    failRef.current = fail;

    const timeoutId = window.setTimeout(() => {
      fail({ code: null, message: 'Timed out loading the payment form. Please try again.' });
    }, renderTimeoutMs);

    window.zuoraCallback = (response: ZCallbackResponse) => {
      if (cancelled) return;
      window.clearTimeout(submitTimeoutRef.current);
      if (response.success === true) {
        setStatus('success');
        setError(null);
        onSuccessRef.current(response.refId);
        return;
      }
      fail({
        responseFrom: response.responseFrom,
        code: response.errorCode ?? null,
        message: response.errorMessage ?? 'Payment submission failed. Please try again.',
      });
    };

    loadZuora()
      .then((Z) => {
        if (cancelled) return;
        Z.setEventHandler('onloadCallback', () => {
          if (cancelled) return;
          window.clearTimeout(timeoutId);
          // A clean render earns a fresh auto-retry budget.
          retryCountRef.current = 0;
          setStatus('ready');
        });
        Z.setEventHandler('onSubmit', () => {
          if (cancelled) return;
          setStatus('submitting');
        });
        Z.render(
          {
            tenantId,
            id: pageId,
            token,
            signature,
            key,
            style: 'inline',
            submitEnabled: 'false',
            url: ZUORA_HPP_SUBMIT_URL,
            field_accountId: accountId,
            field_currency: 'USD',
          },
          {},
          // Render callback: surfaces request-page failures immediately instead
          // of waiting for the timeout. Guarded to request-page so it never
          // double-handles submit-page responses (those arrive via zuoraCallback).
          (response) => {
            if (cancelled) return;
            if (
              response.success === false &&
              response.responseFrom === RESPONSE_FROM_REQUEST_PAGE
            ) {
              fail({
                responseFrom: response.responseFrom,
                code: response.errorCode ?? null,
                message: response.errorMessage ?? 'The payment form could not be loaded.',
              });
            }
          }
        );
      })
      .catch((err: unknown) => {
        fail({
          code: null,
          message: err instanceof Error ? err.message : 'Failed to load the payment form.',
        });
      });

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      window.clearTimeout(submitTimeoutRef.current);
      sandboxObserver?.disconnect();
      delete window.zuoraCallback;
    };
  }, [tenantId, pageId, key, accountId, token, signature, containerId, renderTimeoutMs]);

  const submit = useCallback(() => {
    if (status !== 'ready' || !window.Z) return;
    window.Z.submit();
    // Result returns async via window.zuoraCallback. If it never arrives (bridge
    // stalled), self-detect instead of hanging in `submitting` and route to the
    // failure lane. Disarmed the moment zuoraCallback fires.
    window.clearTimeout(submitTimeoutRef.current);
    submitTimeoutRef.current = window.setTimeout(() => {
      failRef.current?.({
        code: null,
        message: 'Timed out submitting your payment. Please try again.',
      });
    }, submitTimeoutMs);
  }, [status, submitTimeoutMs]);

  return { status, error, submit };
};
